/** @format */

import React, { useMemo } from 'react';
import { TableRow } from '@material-ui/core';
import TableCell from '@material-ui/core/TableCell';
import TemplateButton from '../../../components/Buttons/TemplateButton';
import { Cash100, ChartAreaspline, PlaylistCheck, PlaylistPlay, Receipt } from 'mdi-material-ui';
import I18n from '../../../components/Translate/I18n';
import { InvoiceInterface } from '../../../interfaces/platform/InvoiceInterface';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import { CurrencyHelper } from '../../../helpers/CurrencyHelper';
import { DocumentInterface } from '../../../interfaces/platform/DocumentInterface';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import withStyles from '@material-ui/core/styles/withStyles';
import DownloadButton from '../../../components/Buttons/DownloadButton';
import AcceptButton from '../../../components/Buttons/AcceptButton';
import DeleteButton from '../../../components/Buttons/DeleteButton';
import { KsefError } from './KsefError';
import { VatCollectionInterface } from '../../../interfaces/collections/VatCollectionInterface';
import { findVatByPartialIri } from '../../../modules/document/VatHelper';
import VatInterface from '../../../interfaces/platform/VatInterface';

type InvoiceItemType = {
    classes: any;
    invoice: InvoiceInterface;
    acceptCallback: (id: number) => void;
    openSetAsPaid: (id: number) => void;
    closeSetAsPaid: () => void;
    resendPivotCallback: (id: number) => void;
    cancelClauseCallback: (id: number) => void;
    downloadFileCallback: (document: DocumentInterface, type: string) => void;
    setSelectedReportInvoice: (invoice: InvoiceInterface) => void;
    vats: VatCollectionInterface | undefined;
};

export const InvoiceItem = (props: InvoiceItemType): React.ReactElement => {
    const classes = props.classes;

    const { invoice } = props;
    const pivotDuringResend = useMemo((): boolean => {
        if (invoice.document.additionalParams) {
            const params = JSON.parse(invoice.document.additionalParams);
            return params.pivotDuringResend ? params.pivotDuringResend : false;
        }

        return false;
    }, [invoice]);
    const pivotSynchronized = useMemo((): boolean => {
        if (invoice.document.additionalParams) {
            const params = JSON.parse(invoice.document.additionalParams);
            return params.pivotSynchronized ? params.pivotSynchronized : false;
        }

        return false;
    }, [invoice]);
    const previousPivotId = useMemo((): number => {
        if (invoice.document.additionalParams) {
            const params = JSON.parse(invoice.document.additionalParams);
            return params.previousPivotId ? params.previousPivotId : -1;
        }

        return -1;
    }, [invoice]);

    const vat = useMemo((): VatInterface | undefined => {
        return findVatByPartialIri(props.vats, invoice.vat);
    }, [props.vats, invoice]);

    const tableCellRoot = { root: classes.tableCellRoot };
    const tableRowRoot = { root: classes.tableRowRoot };
    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };

    const tooltipTitle = (invoice: InvoiceInterface): string => {
        if (invoice.type === 3) {
            return 'documents:invoices.cancel_document';
        }
        if (invoice.type === 4) {
            return 'documents:invoices.cancel_clause_generation_failed';
        }

        return 'documents:invoices.cancel_document';
    };

    const cancellable = (invoice: InvoiceInterface): boolean => {
        if(invoice.document.state < 1000 && invoice.document.state >= -1) {
            return false;
        }

        if(!invoice.accepted) {
            return true;
        }

        // if ((invoice.type === 3 || invoice.type === 4) && !invoice.accepted) {
        //     return true;
        // }

        if (invoice.document.state < -1) {
            return true;
        }

        const cancellableStatuses = ["KSEF_ERROR", "KSEF_EMERGENCY_MODE", "KSEF_TOTAL_FAILURE", "REJECTED"];

        if (invoice.ksefStatus && (cancellableStatuses.indexOf(invoice.ksefStatus) > -1)) {
            return true;
        }

        if (invoice.accepted && invoice.acceptReversible) {
            return true;
        }

        return false;
    };

    return (
        <TableRow classes={tableRowRoot}>
            <TableCell classes={tableControlStickyHeader} padding="none" align="left">
                {cancellable(invoice) && (
                    <DeleteButton tooltipTitle={tooltipTitle(invoice)} callback={props.cancelClauseCallback} callbackParam={invoice.id} />
                )}
                {/*{(invoice.document.state < -1 || ((invoice.type === 3 || invoice.type === 4) && !invoice.accepted)) && (*/}
                {/*    <DeleteButton*/}
                {/*        tooltipTitle={*/}
                {/*            invoice.type === 3 || invoice.type === 4*/}
                {/*                ? 'documents:invoices.cancel_document'*/}
                {/*                : 'documents:invoices.cancel_clause_generation_failed'*/}
                {/*        }*/}
                {/*        callback={props.cancelClauseCallback}*/}
                {/*        callbackParam={invoice.id}*/}
                {/*    />*/}
                {/*)}*/}
                {invoice.document.state < 1000 && invoice.document.state >= -1 && (
                    <>
                        <Tooltip title={<I18n trans={'documents:documents.generating'} />}>
                            <CircularProgress className={classes.progress + ' ' + classes.tinyInlineLoader} />
                        </Tooltip>
                    </>
                )}
                {invoice.document.state >= 1000 && (
                    <>
                        {!invoice.accepted && !invoice.ksefStatus && (
                            <>
                                <AcceptButton tooltipTitle={'documents:invoices.accept'} callback={props.acceptCallback} callbackParam={invoice.id} />
                            </>
                        )}
                        {invoice.document.downloadHash && (
                            <>
                                <DownloadButton
                                    tooltipTitle={'documents:invoices.download_invoice'}
                                    callback={(data: { document: DocumentInterface; type: string }): void => {
                                        props.downloadFileCallback(data.document, data.type);
                                    }}
                                    callbackParam={{ document: invoice.document, type: 'document_set' }}
                                />
                                {invoice.blockId !== -2 && (
                                    <TemplateButton
                                        icon={Receipt}
                                        tooltipTitle={'documents:invoices.download_specification'}
                                        callback={(data: { document: DocumentInterface; type: string }): void => {
                                            props.downloadFileCallback(data.document, data.type);
                                        }}
                                        callbackParam={{ document: invoice.document, type: 'specification_by_document' }}
                                    />
                                )}
                            </>
                        )}
                        {invoice.pivotId && (
                            <>
                                <TemplateButton
                                    icon={PlaylistCheck}
                                    tooltipTitle={'documents:invoices.download_pivot'}
                                    callback={(data: { document: DocumentInterface; type: string }): void => {
                                        props.downloadFileCallback(data.document, data.type);
                                    }}
                                    callbackParam={{ document: invoice.document, type: 'pivot_by_document' }}
                                />
                            </>
                        )}
                        {(pivotSynchronized || previousPivotId !== -1) && (
                            <TemplateButton
                                icon={PlaylistPlay}
                                color={pivotDuringResend ? 'success' : ''}
                                tooltipTitle={pivotDuringResend ? 'documents:invoices.cancel_resend_pivot' : 'documents:invoices.resend_pivot'}
                                callback={props.resendPivotCallback}
                                callbackParam={invoice.id}
                            />
                        )}
                    </>
                )}
                {(invoice.type === 3 || invoice.type === 4) && (
                    <>
                        <TemplateButton
                            icon={ChartAreaspline}
                            tooltipTitle={'documents:invoices.check_report'}
                            callback={(data: { invoice: InvoiceInterface }): void => {
                                props.setSelectedReportInvoice(data.invoice);
                            }}
                            callbackParam={{ invoice: invoice }}
                        />
                    </>
                )}
                {invoice.accepted && !invoice.paidOn && (
                    <TemplateButton
                        color={'warning'}
                        icon={Cash100}
                        tooltipTitle={'documents:invoices.set_paid_on'}
                        callback={props.openSetAsPaid}
                        callbackParam={invoice.id}
                    />
                )}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                <I18n trans={'documents:invoices.type.' + invoice.type} />
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {DateHelperTs.format(invoice.soldOn, 'YYYY-MM-DD')}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {DateHelperTs.format(invoice.soldFor, 'MMMM')} {DateHelperTs.format(invoice.soldFor, 'YYYY')}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.invoiceNumber}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.seller?.mainname}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.buyer?.mainname}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.agreement}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {(invoice.type === 3 || invoice.type === 4) && DateHelperTs.format(invoice.paymentDeadline, 'YYYY-MM-DD')}
                {invoice.type !== 3 &&
                    invoice.type !== 4 &&
                    invoice.paymentDeadlineClause &&
                    !invoice.paymentDeadlineClause.includes('DD') &&
                    invoice.paymentDeadline &&
                    DateHelperTs.format(invoice.paymentDeadline, 'YYYY-MM-DD')}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.paymentDeadlineClause}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.document.state < 1000 && invoice.document.state >= 0 && (
                    <Tooltip title={<I18n trans={'documents:documents.generating'} />}>
                        <CircularProgress className={classes.progress + ' ' + classes.tinyInlineLoader} />
                    </Tooltip>
                )}
                {invoice.document.state >= 1000 && CurrencyHelper.numericToStandardPln(invoice.amount)}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {vat && vat.description}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.document.state < 1000 && invoice.document.state >= 0 && (
                    <Tooltip title={<I18n trans={'documents:documents.generating'} />}>
                        <CircularProgress className={classes.progress + ' ' + classes.tinyInlineLoader} />
                    </Tooltip>
                )}
                {invoice.document.state >= 1000 && CurrencyHelper.numericToStandardPln(invoice.taxAmount)}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.document.state < 1000 && invoice.document.state >= 0 && (
                    <Tooltip title={<I18n trans={'documents:documents.generating'} />}>
                        <CircularProgress className={classes.progress + ' ' + classes.tinyInlineLoader} />
                    </Tooltip>
                )}
                {invoice.document.state >= 1000 && CurrencyHelper.numericToStandardPln(invoice.noTaxAmount)}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.paidOn && DateHelperTs.format(invoice.paidOn, 'YYYY-MM-DD')}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.accepted && <I18n trans={'general:common.yes'} />}
                {!invoice.accepted && <I18n trans={'general:common.no'} />}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.accepted && invoice.acceptedOn && DateHelperTs.format(invoice.acceptedOn, 'YYYY-MM-DD')}
            </TableCell>
            {/*<TableCell classes={tableCellRoot} padding="none" align="center">*/}
            {/*    {invoice.deleted && <I18n trans={'general:common.yes'} />}*/}
            {/*    {!invoice.deleted && <I18n trans={'general:common.no'} />}*/}
            {/*</TableCell>*/}
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.pivotId && <I18n trans={'general:common.yes'} />}
                {!invoice.pivotId && <I18n trans={'general:common.no'} />}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.iafId && <I18n trans={'general:common.yes'} />}
                {!invoice.iafId && <I18n trans={'general:common.no'} />}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.createdAt && DateHelperTs.format(invoice.createdAt, 'YYYY-MM-DD HH:mm:ss')}
            </TableCell>

            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.ksefStatus && <I18n trans={'documents:invoices.ksef_statuses.' + invoice.ksefStatus} />}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.ksefPackageStatus && <I18n trans={'documents:invoices.ksef_package_statuses.' + invoice.ksefPackageStatus} />}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.ksefLastUpdate && DateHelperTs.format(invoice.ksefLastUpdate, 'YYYY-MM-DD HH:mm:ss')}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.ksefId}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                {invoice.ksefInvoiceNumber}
            </TableCell>
            <TableCell classes={tableCellRoot} padding="none" align="left">
                <KsefError classes={classes} invoice={invoice} />
            </TableCell>
        </TableRow>
    );
};

export default withStyles(oplAppStyle)(InvoiceItem);
